import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../services/api';
import {
  MasterCalendar,
  ExecutionSchedule,
  ViewMode,
  ScheduleAPIResponse,
} from '../types';
import { ScheduleCalendar } from './ScheduleCalendar';
import { VerticalTimeline } from './VerticalTimeline';
import { addDays, subDays } from 'date-fns';

interface ScheduleDashboardProps {
  activeWorkerId: string | null;
  prefetchedScheduleCache?: {
    workerURL: string;
    jwt: string;
    timestamp: number;
    workerId: string;
  } | null;
}

export const ScheduleDashboard: React.FC<ScheduleDashboardProps> = ({ activeWorkerId, prefetchedScheduleCache }) => {
  const { t } = useTranslation('schedules');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workerUnavailable, setWorkerUnavailable] = useState(false);
  const [workerURL, setWorkerURL] = useState<string | null>(null);
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [masterCalendar, setMasterCalendar] = useState<MasterCalendar | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // Nonce guard: each fetch invocation captures its own nonce. Before any state
  // update that affects the displayed data, we check that the nonce still matches
  // `fetchNonce.current`. If the user switched workers mid-flight, the nonce will
  // have been incremented by the new invocation, so stale results are discarded.
  const fetchNonce = useRef(0);

  // Cache worker URL + JWT per-worker (tự hủy sau 50 phút)
  const [cachedWorkerURL, setCachedWorkerURL] = useState<string | null>(null);
  const [cachedJWT, setCachedJWT] = useState<string | null>(null);
  const [cacheTimestamp, setCacheTimestamp] = useState<number | null>(null);
  const [cachedWorkerId, setCachedWorkerId] = useState<string | null>(null);

  // Account name mapping: account_id -> { full_name, username, email }
  const [accountNameMap, setAccountNameMap] = useState<Record<string, { full_name?: string; username?: string; email?: string }>>({});

  // Fetch accounts for name mapping
  useEffect(() => {
    (async () => {
      try {
        const response = await apiService.getAccounts();
        const accountsList = response?.data?.accounts || response?.accounts || [];
        const nameMap: Record<string, { full_name?: string; username?: string; email?: string }> = {};
        for (const acc of accountsList) {
          const id = acc._id || acc.id;
          if (id) {
            nameMap[id] = {
              full_name: acc.full_name || acc.fullName || acc.name,
              username: acc.username,
              email: acc.email,
            };
          }
        }
        setAccountNameMap(nameMap);
      } catch (err) {
        console.error('Failed to fetch account names:', err);
      }
    })();
  }, []);

  // Enrich timeline executions with account names
  const enrichedTimeline = useMemo(() => {
    if (!masterCalendar?.timeline) return [];
    return masterCalendar.timeline.map((exec) => {
      const account = accountNameMap[exec.account_id];
      if (account) {
        const displayName = account.full_name || account.username || account.email || exec.account_id;
        return { ...exec, account_name: displayName };
      }
      return exec;
    });
  }, [masterCalendar?.timeline, accountNameMap]);

  // Extract unique accounts from timeline for filter dropdown
  const uniqueAccounts = useMemo(() => {
    const accountMap = new Map<string, string>();
    for (const exec of enrichedTimeline) {
      if (!accountMap.has(exec.account_id)) {
        accountMap.set(exec.account_id, exec.account_name || exec.account_id);
      }
    }
    return Array.from(accountMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [enrichedTimeline]);

  // Filter timeline by selected account
  const filteredTimeline = useMemo(() => {
    if (!selectedAccountId) return enrichedTimeline;
    return enrichedTimeline.filter((exec) => exec.account_id === selectedAccountId);
  }, [enrichedTimeline, selectedAccountId]);

  const isWorkerUnavailableError = (err: any): boolean => {
    const message = String(err?.message || '');
    return err?.status === 503 || err?.isWorkerUnavailable === true || /503|service unavailable/i.test(message);
  };

  // Fetch worker URL and schedule data
  const fetchScheduleData = useCallback(async () => {
    if (!activeWorkerId) return;

    setLoading(true);
    setError(null);
    setWorkerUnavailable(false);

    // Each invocation gets a unique nonce. Any state update that affects
    // displayed data is guarded by this nonce so that a stale in-flight
    // request for a previously-selected worker cannot overwrite the results
    // for the currently-selected worker.
    const nonce = ++fetchNonce.current;

    try {
      let workerUrl: string;
      let jwt: string;

      // Check prefetched cache from App.tsx first, then local cache
      const now = Date.now();
      const prefetchValid = prefetchedScheduleCache &&
        prefetchedScheduleCache.workerId === activeWorkerId &&
        (now - prefetchedScheduleCache.timestamp) < 3000000;
      const localCacheValid = cacheTimestamp && (now - cacheTimestamp) < 3000000 && cachedWorkerId === activeWorkerId;

      if (cachedWorkerURL && cachedJWT && localCacheValid) {
        // Use local cache (already fetched on this page)
        console.log('♻️  Using local cached worker URL for', activeWorkerId, '(valid for', Math.round((3000000 - (now - cacheTimestamp!)) / 60000), 'more minutes)');
        workerUrl = cachedWorkerURL;
        jwt = cachedJWT;
      } else if (prefetchValid) {
        // Use prefetched cache from App.tsx (fetched on home page)
        console.log('♻️  Using prefetched worker URL for', activeWorkerId, '(valid for', Math.round((3000000 - (now - prefetchedScheduleCache!.timestamp)) / 60000), 'more minutes)');
        workerUrl = prefetchedScheduleCache!.workerURL;
        jwt = prefetchedScheduleCache!.jwt;

        // Sync to local cache
        setCachedWorkerURL(workerUrl);
        setCachedJWT(jwt);
        setCacheTimestamp(prefetchedScheduleCache!.timestamp);
        setCachedWorkerId(activeWorkerId);
      } else {
        // Fetch mới từ backend
        console.log('🔗 Fetching fresh worker URL for worker', activeWorkerId);
        const urlResponse: ScheduleAPIResponse = await apiService.getWorkerScheduleURL(activeWorkerId);
        const { worker, jwt_token } = urlResponse;

        workerUrl = worker.url;
        jwt = jwt_token || '';

        // Lưu cache với timestamp (dù user đã switch worker, cache này vẫn có ích
        // khi switch lại về worker này sau)
        setCachedWorkerURL(workerUrl);
        setCachedJWT(jwt);
        setCacheTimestamp(Date.now());
        setCachedWorkerId(activeWorkerId);
        console.log('✅ Worker URL cached for', activeWorkerId, '(expires in 50 minutes)');

        // Abort nếu user đã switch sang worker khác trong lúc fetch URL
        if (nonce !== fetchNonce.current) {
          console.log('⚠️  Worker switched mid-fetch — discarding result for', activeWorkerId);
          return;
        }

        // Đợi 3s cho worker ready (chỉ khi fetch mới)
        console.log('⏳ Waiting 3s for worker to be ready...');
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      // Check lại sau resolve URL — user có thể đã switch trong khi đợi
      if (nonce !== fetchNonce.current) {
        console.log('⚠️  Worker switched after URL resolution — discarding result for', activeWorkerId);
        return;
      }

      setWorkerURL(workerUrl);
      setJwtToken(jwt);

      // Retry 5 lần để fetch schedule
      const maxRetries = 5;
      let lastError: any = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        // Dừng retry nếu user đã switch sang worker khác
        if (nonce !== fetchNonce.current) {
          console.log('⚠️  Worker switched mid-retry — aborting for', activeWorkerId);
          return;
        }

        try {
          console.log(`📡 [${activeWorkerId.slice(-8)}] Attempt ${attempt}/${maxRetries}: Fetching schedule...`);

          const calendar: MasterCalendar = await apiService.getMasterCalendar(workerUrl, jwt);

          // Check nonce BEFORE applying result — discard if worker changed
          if (nonce !== fetchNonce.current) {
            console.log('⚠️  Worker switched after getMasterCalendar — discarding result for', activeWorkerId);
            return;
          }

          setMasterCalendar(calendar);
          setSelectedDate(new Date());

          console.log('✅ Schedule loaded successfully for worker', activeWorkerId);
          lastError = null;
          break; // Success - exit retry loop

        } catch (err: any) {
          lastError = err;
          console.warn(`⚠️  Attempt ${attempt}/${maxRetries} failed:`, err.message);

          if (isWorkerUnavailableError(err)) {
            break;
          }

          // Nếu chưa hết retries, đợi rồi thử lại
          if (attempt < maxRetries) {
            const backoffDelay = 3000 * attempt; // 3s, 6s, 9s, 12s
            console.log(`⏳ Waiting ${backoffDelay}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
          }
        }
      }

      // Nếu fail hết 5 lần, throw error
      if (lastError) {
        throw lastError;
      }

    } catch (err: any) {
      if (nonce !== fetchNonce.current) return; // stale error — discard
      console.error('❌ Failed to fetch schedule data:', err);
      if (isWorkerUnavailableError(err)) {
        setWorkerUnavailable(true);
        setError('Worker service unavailable (503)');
      } else {
        const errMsg = String(err?.message || err?.toString() || '');
        const isEmptyScheduleError =
          err?.status === 404 ||
          err?.response?.status === 404 ||
          errMsg.toLowerCase().includes('no schedules generated yet');

        if (isEmptyScheduleError) {
          console.log('🔄 Empty schedule detected, displaying empty schedule state');
          setMasterCalendar({
            week: '-',
            generated_at: new Date().toISOString(),
            total_executions: 0,
            accounts: [],
            timeline: [],
            statistics: { total_executions: 0, total_accounts: 0, by_status: {}, user_tasks: 0, internal_tasks: 0 }
          });
          setError(null);
        } else {
          setError(err.message || 'Failed to fetch');
        }
      }
    } finally {
      // Chỉ tắt loading nếu đây là fetch hiện tại (không tắt loading cho fetch mới hơn)
      if (nonce === fetchNonce.current) {
        setLoading(false);
      }
    }
  }, [activeWorkerId, cachedWorkerURL, cachedJWT, cacheTimestamp, cachedWorkerId, prefetchedScheduleCache]);

  // Clear stale data when switching workers
  useEffect(() => {
    if (activeWorkerId) {
      setMasterCalendar(null);
      setWorkerURL(null);
      setJwtToken(null);
      setError(null);
      setWorkerUnavailable(false);
      setSelectedDate(new Date());
      fetchScheduleData();
    }
  }, [activeWorkerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh schedules every 30 seconds (only when page visible)
  useEffect(() => {
    if (!activeWorkerId || !workerURL || !jwtToken) return;

    const intervalId = setInterval(async () => {
      // Skip polling if page not visible (user switched tab/window)
      if (document.hidden) {
        console.log('⏸️  Skipping poll - page not visible');
        return;
      }

      try {
        // Silent refresh - don't show loading state
        const calendar: MasterCalendar = await apiService.getMasterCalendar(
          workerURL,
          jwtToken
        );
        setMasterCalendar(calendar);
      } catch (err) {
        console.error('Auto-refresh failed:', err);
        // Don't update error state to avoid disrupting user
      }
    }, 30000); // 30 seconds

    // Cleanup on unmount (when leaving /schedules page)
    return () => {
      console.log('🛑 Stopping schedule polling - component unmounted');
      clearInterval(intervalId);
    };
  }, [activeWorkerId, workerURL, jwtToken]);

  // Refresh schedule data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchScheduleData();
    setRefreshing(false);
  };

  // Rebuild schedules
  const handleRebuild = async () => {
    if (!workerURL || !jwtToken) return;

    try {
      setRefreshing(true);
      await apiService.rebuildSchedules(workerURL, jwtToken);
      // Refetch after rebuild
      await fetchScheduleData();
    } catch (err: any) {
      console.error('Failed to rebuild schedules:', err);
      alert('Failed to rebuild schedules: ' + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  // No worker selected state
  if (!activeWorkerId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-gray-400 text-5xl mb-4">👈</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('noWorkerSelected')}</h2>
          <p className="text-gray-600">{t('noSchedulesDesc')}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (workerUnavailable) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-yellow-500 text-5xl mb-4">🛠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('workerUnavailable')}</h2>
          <p className="text-gray-600 mb-4">{t('workerUnavailableDesc')}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('error')}</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!masterCalendar) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Worker Schedule</h1>
            <p className="text-gray-600 mt-1">
              {masterCalendar.statistics?.user_tasks ?? masterCalendar.total_executions} task{(masterCalendar.statistics?.user_tasks ?? masterCalendar.total_executions) !== 1 ? 's' : ''} scheduled
              for week {masterCalendar.week}
              {masterCalendar.statistics?.user_tasks && (
                <span className="text-sm text-gray-500 ml-2">
                  ({masterCalendar.total_executions} total including internal)
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <span className={refreshing ? 'animate-spin' : ''}>🔄</span>
              {t('refresh')}
            </button>
            <button
              onClick={handleRebuild}
              disabled={refreshing}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              Rebuild Schedules
            </button>
          </div>
        </div>

        {/* View Mode Selector + Account Filter */}
        <div className="flex items-center justify-between mb-4">
          <div data-tour="schedule-toggle" className="flex gap-2">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'calendar'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {t('viewCalendar')}
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${viewMode === 'timeline'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {t('viewTimeline')}
            </button>
          </div>

          {/* Account Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">👤 Account:</span>
            <select
              value={selectedAccountId || ''}
              onChange={(e) => setSelectedAccountId(e.target.value || null)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[240px]"
            >
              <option value="">{t('allAccounts')} ({uniqueAccounts.length})</option>
              {uniqueAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name}
                </option>
              ))}
            </select>
            {selectedAccountId && (
              <button
                onClick={() => setSelectedAccountId(null)}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
          <button
            onClick={() => setSelectedDate(subDays(selectedDate, 1))}
            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            ◀ Previous Day
          </button>
          <button
            onClick={() => setSelectedDate(new Date())}
            className="px-4 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors"
          >
            {t('today')}
          </button>
          <button
            onClick={() => setSelectedDate(addDays(selectedDate, 1))}
            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            Next Day ▶
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-100 rounded-lg p-4">
          <div className="text-2xl mb-1">⏳</div>
          <div className="text-2xl font-bold text-gray-800">
            {filteredTimeline.filter((e) => e.status === 'pending').length}
          </div>
          <div className="text-sm text-gray-600">Pending</div>
        </div>
        <div className="bg-blue-100 rounded-lg p-4">
          <div className="text-2xl mb-1">▶️</div>
          <div className="text-2xl font-bold text-blue-800">
            {filteredTimeline.filter((e) => e.status === 'running').length}
          </div>
          <div className="text-sm text-blue-600">Running</div>
        </div>
        <div className="bg-green-100 rounded-lg p-4">
          <div className="text-2xl mb-1">✅</div>
          <div className="text-2xl font-bold text-green-800">
            {filteredTimeline.filter((e) => e.status === 'completed').length}
          </div>
          <div className="text-sm text-green-600">Completed</div>
        </div>
        <div className="bg-red-100 rounded-lg p-4">
          <div className="text-2xl mb-1">❌</div>
          <div className="text-2xl font-bold text-red-800">
            {filteredTimeline.filter((e) => e.status === 'failed').length}
          </div>
          <div className="text-sm text-red-600">Failed</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {viewMode === 'calendar' ? (
          <ScheduleCalendar
            executions={filteredTimeline}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
        ) : (
          <VerticalTimeline
            executions={filteredTimeline}
            selectedDate={selectedDate}
          />
        )}
      </div>
    </div>
  );
};
